import { PersonalityEffect, PersonalityQuestion, questionList } from './questions';

// 仕様書 6章の暫定閾値。memorii アプリ本体（lib/9.type/personality.dart）と共通。
export const personalityHighThreshold = 5.5;
export const personalityLowThreshold = 3.5;
export const personalityDominantDiff = 1.5;

export type AxisState = 'leftDominant' | 'rightDominant' | 'bothHigh' | 'bothLow' | 'balanced';

export type AxisResult = {
  state: AxisState;
  direction: string; // より高い側（同点は left 優先）の特性コード
  difference: number; // left - right の絶対値
};

export type AxisKey = 'H_C' | 'S_R' | 'T_E' | 'J_P';

export const axisDefs: { key: AxisKey; left: PersonalityEffect; right: PersonalityEffect }[] = [
  { key: 'H_C', left: 'H', right: 'C' },
  { key: 'S_R', left: 'S', right: 'R' },
  { key: 'T_E', left: 'T', right: 'E' },
  { key: 'J_P', left: 'J', right: 'P' },
];

/** 仕様書 7章の判定ロジックそのまま。 */
export function classifyAxis(left: number, right: number, leftLabel: string, rightLabel: string): AxisResult {
  const diff = left - right;
  const direction = diff >= 0 ? leftLabel : rightLabel;

  if (left >= personalityHighThreshold && right >= personalityHighThreshold) {
    return { state: 'bothHigh', direction, difference: Math.abs(diff) };
  }
  if (left <= personalityLowThreshold && right <= personalityLowThreshold) {
    return { state: 'bothLow', direction, difference: Math.abs(diff) };
  }
  if (diff >= personalityDominantDiff) {
    return { state: 'leftDominant', direction: leftLabel, difference: Math.abs(diff) };
  }
  if (diff <= -personalityDominantDiff) {
    return { state: 'rightDominant', direction: rightLabel, difference: Math.abs(diff) };
  }
  return { state: 'balanced', direction, difference: Math.abs(diff) };
}

/** Firestore保存用の状態コード（仕様書11章）。web版では表示・分析用に据え置き。 */
export function axisStateCode(state: AxisState): string {
  switch (state) {
    case 'leftDominant': return 'LEFT_DOMINANT';
    case 'rightDominant': return 'RIGHT_DOMINANT';
    case 'bothHigh': return 'BOTH_HIGH';
    case 'bothLow': return 'BOTH_LOW';
    case 'balanced': return 'BALANCED';
  }
}

export const personalityTraitShortName: Record<string, string> = {
  H: '専門性', C: '関係性', T: 'サポート', E: '自律', S: '安定', R: '挑戦', J: '計画', P: '柔軟',
};

export const personalityTraitFullName: Record<string, string> = {
  H: '専門性・完成度志向', C: '関係性・顧客志向', S: '安定・着実志向', R: '挑戦・変化志向',
  T: 'サポート成長志向', E: '自律成長志向', J: '計画・構造志向', P: '柔軟・適応志向',
};

export function traitsForType(type: string): string[] {
  return type
    .split('')
    .map((code) => personalityTraitFullName[code])
    .filter((v): v is string => Boolean(v));
}

export const axisStateMessages: Record<AxisKey, Record<AxisState, string>> = {
  H_C: {
    leftDominant: '専門性・完成度を強く追求する',
    rightDominant: '人との関係や顧客理解を強く重視する',
    bothHigh: '技術も人との関係も、どちらも大切にする',
    bothLow: '技術・関係性のどちらにも強く偏らない',
    balanced: '専門性と関係性をバランスよく見る',
  },
  S_R: {
    leftDominant: '安定した環境で力を発揮しやすい',
    rightDominant: '変化や新しい挑戦を楽しみやすい',
    bothHigh: '安定した土台を持ちながら挑戦も楽しめる',
    bothLow: '安定・変化のどちらにも強くこだわらない',
    balanced: '安定と挑戦をバランスよく取り入れる',
  },
  T_E: {
    leftDominant: 'サポートのある環境で伸びやすい',
    rightDominant: '自分で試せる環境で伸びやすい',
    bothHigh: '教わることも、自分で試すことも得意',
    bothLow: '学び方への強いこだわりは少ない',
    balanced: '支援と自律を状況に合わせて使い分ける',
  },
  J_P: {
    leftDominant: '計画を立てて進めるのが得意',
    rightDominant: '状況に合わせて柔軟に動くのが得意',
    bothHigh: '計画もでき、状況変化にも柔軟に対応できる',
    bothLow: '進め方への強いこだわりは少ない',
    balanced: '計画と柔軟対応を使い分ける',
  },
};

export function describeAxisState(axisKey: AxisKey, result: AxisResult): string {
  const base = axisStateMessages[axisKey]?.[result.state] ?? '';
  if (!base) return base;
  if (result.state === 'leftDominant' || result.state === 'rightDominant') return base;
  if (result.difference < 0.05) return base;
  const directionName = personalityTraitShortName[result.direction];
  if (!directionName) return base;
  return `${base}（やや${directionName}寄り）`;
}

export type PersonalityAnswer = number | null;

/** 32問・8特性。各質問は自分の effect 特性のみに加点。特性ごとに4問の平均（1.0〜7.0）を返す。 */
export function calculateRawScores(
  questions: PersonalityQuestion[],
  selectedAnswers: PersonalityAnswer[],
): Record<PersonalityEffect, number> {
  const answersByEffect: Record<PersonalityEffect, number[]> = {
    H: [], C: [], S: [], R: [], T: [], E: [], J: [], P: [],
  };

  questions.forEach((q, i) => {
    const answer = selectedAnswers[i];
    if (answer == null) return;
    answersByEffect[q.effect].push(answer);
  });

  const result = {} as Record<PersonalityEffect, number>;
  (Object.keys(answersByEffect) as PersonalityEffect[]).forEach((effect) => {
    const answers = answersByEffect[effect];
    result[effect] = answers.length === 0
      ? 4.0
      : answers.reduce((a, b) => a + b, 0) / answers.length;
  });
  return result;
}

export type PersonalityDiagnosisResult = {
  type: string;
  results: number[]; // [H_C%, S_R%, T_E%, J_P%]（左側の割合）
  rawScores: Record<PersonalityEffect, number>;
  axisStates: Record<AxisKey, AxisResult>;
};

/** 32問の全回答から、タイプ・4軸の比率・軸の状態を算出する。 */
export function computeDiagnosisResult(selectedAnswers: PersonalityAnswer[]): PersonalityDiagnosisResult {
  const rawScores = calculateRawScores(questionList, selectedAnswers);

  const calcRatio = (left: PersonalityEffect, right: PersonalityEffect): number => {
    const leftScore = rawScores[left];
    const rightScore = rawScores[right];
    const total = leftScore + rightScore;
    let leftRatio = total === 0 ? 50.0 : (leftScore / total) * 100;
    const rightRatio = 100 - leftRatio;
    if (Math.abs(leftRatio - rightRatio) < 0.0001) leftRatio = 51.0;
    return leftRatio;
  };

  const pickType = (left: PersonalityEffect, right: PersonalityEffect): string =>
    rawScores[left] >= rawScores[right] ? left : right;

  const type = axisDefs.map(({ left, right }) => pickType(left, right)).join('');
  const results = axisDefs.map(({ left, right }) => calcRatio(left, right));

  const axisStates = {} as Record<AxisKey, AxisResult>;
  axisDefs.forEach(({ key, left, right }) => {
    axisStates[key] = classifyAxis(rawScores[left], rawScores[right], left, right);
  });

  return { type, results, rawScores, axisStates };
}
